// src/hooks/raceway/useRacewayWebSocket.ts
'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { Estado, TankSnapshot, ValveState } from '@/types/proyectos/raceway';

interface WebSocketState {
  estado: Estado | null;
  error: string | null;
  ultimaActualizacion: string | null;
}

/**
 * Hook that connects to the Raceway WebSocket and parses the payload.
 *
 * Expected WS payload format (from Rust → Python → browser):
 * {
 *   "tanks": {
 *     "dep_raceway": { name, level, state, auto_control, current_phase, busy,
 *                      sensor_drain, sensor_max, sensor_min, phases },
 *     "dep_cultivo":  { ... },
 *     "dep_cosecha":  { ... }
 *   },
 *   "valves": [{ addr: 16, open: false }, { addr: 17, open: false }, ...]
 * }
 */
export function useRacewayWebSocket(url: string | null) {
  const [state, setState] = useState<WebSocketState>({
    estado: null,
    error: null,
    ultimaActualizacion: null,
  });

  const wsRef              = useRef<WebSocket | null>(null);
  const reconnectAttempts  = useRef<number>(0);
  const lastUpdate         = useRef<number>(0);
  const mounted            = useRef<boolean>(true);
  const reconnectTimeout   = useRef<NodeJS.Timeout | undefined>(undefined);

  /**
   * Parses the raw WS message into a typed Estado.
   * The payload arrives flat: { tanks: {...}, valves: [...] }
   */
  const parseEstado = useCallback((data: unknown): Estado | null => {
    const d = data as Record<string, unknown>;
    const tanks  = d?.tanks  as Record<string, unknown> | undefined;
    const valves = (d?.valves as ValveState[] | undefined) ?? [];

    if (!tanks?.dep_raceway || !tanks?.dep_cultivo || !tanks?.dep_cosecha) {
      return null;
    }

    return {
      tanks: {
        dep_raceway: tanks.dep_raceway as TankSnapshot,
        dep_cultivo: tanks.dep_cultivo as TankSnapshot,
        dep_cosecha: tanks.dep_cosecha as TankSnapshot,
      },
      valves,
    };
  }, []);

  const connect = useCallback(() => {
    if (!mounted.current) return;

    // Close any stale connection without triggering reconnect logic
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    // No URL → no connection (access guard in page)
    if (!url) {
      setState(prev => ({ ...prev, error: null }));
      return;
    }

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mounted.current) return;
        reconnectAttempts.current = 0;
        setState(prev => ({ ...prev, error: null }));
      };

      ws.onmessage = (event: MessageEvent) => {
        if (!mounted.current) return;

        // Throttle: max one update per 100 ms
        const now = Date.now();
        if (now - lastUpdate.current < 100) return;
        lastUpdate.current = now;

        try {
          const message: unknown = JSON.parse(event.data as string);
          const parsed = parseEstado(message);
          if (parsed) {
            setState({
              estado: parsed,
              ultimaActualizacion: new Date().toLocaleTimeString('es-ES'),
              error: null,
            });
          }
        } catch {
          // Silently ignore malformed frames
        }
      };

      ws.onerror = () => {
        if (mounted.current) {
          setState(prev => ({ ...prev, error: 'Error de conexión WebSocket' }));
        }
      };

      ws.onclose = () => {
        if (!mounted.current) return;

        const attempt = reconnectAttempts.current + 1;
        reconnectAttempts.current = attempt;

        if (attempt <= 10) {
          // Exponential backoff: 2 s, 4 s, 8 s … max 30 s
          const delay = Math.min(1000 * Math.pow(2, attempt), 30_000);
          reconnectTimeout.current = setTimeout(connect, delay);
        } else {
          setState(prev => ({
            ...prev,
            error: 'No se pudo conectar. Recargue la página.',
          }));
        }
      };
    } catch {
      reconnectTimeout.current = setTimeout(connect, 3_000);
    }
  }, [url, parseEstado]);

  useEffect(() => {
    mounted.current = true;
    connect();

    return () => {
      mounted.current = false;
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return state;
}